// Shared inner body for the Oscar account notification inbox/archive list page.
// Renders inside AccountPageBody (the account side-nav shell) without the
// document chrome -- each site wraps this in its own PageSkeleton so the DOM is
// byte-identical to the pre-move AccountPageShell.
import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

import { CSRFToken } from "@reactivated/forms";

import { AccountPageBody } from "../AccountPageBody";
import { Pagination } from "../Pagination";

type IProps = Immutable<templates.NotificationListDocumentTemplate>;

type INotification =
    templates.NotificationListDocumentTemplate["notifications"][number];

// Stock list.html renders notification.subject through `|safe` (DB-stored HTML).
const NotificationSubject = ({
    notification,
}: {
    notification: INotification;
}): React.JSX.Element => {
    const subject = (
        <span dangerouslySetInnerHTML={{ __html: notification.subject_html }} />
    );
    return notification.is_read ? subject : <strong>{subject}</strong>;
};

export const NotificationListBody = (props: IProps): React.JSX.Element => {
    const {
        list_type,
        inbox_url,
        archive_url,
        inbox_label,
        archive_label,
        update_url,
        notifications,
        pagination,
        empty_message,
        view_label,
        with_selected_label,
        archive_button_label,
        archiving_label,
        delete_button_label,
        deleting_label,
    } = props;
    const isInbox = list_type === "inbox";
    return (
        <AccountPageBody sideNav={props.side_nav}>
            <ul className="nav nav-tabs">
                <li className="nav-item">
                    <a
                        href={inbox_url}
                        className={`nav-link${isInbox ? " active" : ""}`}
                    >
                        {inbox_label}
                    </a>
                </li>
                <li className="nav-item">
                    <a
                        href={archive_url}
                        className={`nav-link${!isInbox ? " active" : ""}`}
                    >
                        {archive_label}
                    </a>
                </li>
            </ul>

            {notifications.length > 0 ? (
                <>
                    {pagination && <Pagination data={pagination} />}

                    <form action={update_url} method="post">
                        <CSRFToken />
                        <table className="table table-striped table-bordered">
                            <tbody>
                                {notifications.map((notification, i) => (
                                    <tr key={i}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                name="selected_notification"
                                                value={notification.id}
                                            />
                                        </td>
                                        <td>
                                            {notification.is_read ? (
                                                <i className="fas fa-inbox"></i>
                                            ) : (
                                                <i className="fas fa-envelope"></i>
                                            )}
                                        </td>
                                        <td>
                                            <NotificationSubject
                                                notification={notification}
                                            />
                                            <br />
                                            <em>{notification.date_sent}</em>
                                        </td>
                                        <td>
                                            <a
                                                href={notification.detail_url}
                                                className="btn btn-info btn-sm"
                                            >
                                                {view_label}
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {with_selected_label}
                        {isInbox && (
                            <button
                                type="submit"
                                className="btn btn-success"
                                name="action"
                                value="archive"
                                data-loading-text={archiving_label}
                            >
                                {archive_button_label}
                            </button>
                        )}
                        <button
                            type="submit"
                            className="btn btn-danger"
                            name="action"
                            value="delete"
                            data-loading-text={deleting_label}
                        >
                            {delete_button_label}
                        </button>
                    </form>

                    {pagination && <Pagination data={pagination} />}
                </>
            ) : (
                <p className="mt-3">{empty_message}</p>
            )}
        </AccountPageBody>
    );
};
